import * as React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import authService from './api-authorization/AuthorizeService';
import Modal from 'react-bootstrap/Modal';

interface JobDetailsTransferJobModalProps {
    jobId: number;
    systemCompanyId: number;
    customers: CustomerList[];
    showModal: boolean;
    hideModal(): void;
    saveModal(customerContacts: CustomerContactList[], jobId: number): void;
}

interface CustomerList {
    id: number;
    name: string;
    gateCode: string;
    accountId: string;
    accountIdDunny: string;
    accountTypeId: number;
    accountType: string;
    accountLimitFormatted: string;
    allowOnGoingHire: boolean;
    quoteNotesDunny: string;
    canEmailCustomer: boolean;
    cashSaleAccount: boolean;
    statusTypeId: number;
    locked: boolean;
}

interface CustomerContactList {
    id: number;
    name: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    mobile: string;
}

interface ItemListItem {
    jobQuoteItemId: number;
    itemId: number;
    item: string;
    itemTypeId: number;
    invoicePeriodTypeId: number;
    installOnly: boolean;
    quantity: number;
    hirePeriod: number;
    defaultHirePeriod: number;
    purchaseOrderNumber: string;
    amount: number;
    overhireRate: number;
}

interface AddContact {
    firstName: string;
    lastName: string;
    email: string;
    mobile: string;
    phone: string;
}

interface CashSaleCustomerContact {
    customerContactName: string;
    customerContactPhone: string;
    customerContactEmail: string;
}

const JobDetailsTransferJobModalData = (props: JobDetailsTransferJobModalProps) => {
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [saveDisabled, setSaveDisabled] = React.useState(false);
    const [customerContacts, setCustomerContacts] = React.useState<CustomerContactList[]>([]);
    const [customerId, setCustomerId] = React.useState(-1);
    const [customerContactId, setCustomerContactId] = React.useState(-1);
    const [customerIsCashSale, setCustomerIsCashSale] = React.useState(false);
    const [cashSaleContact, setCashSaleContact] = React.useState<CashSaleCustomerContact>({
        customerContactName: "",
        customerContactPhone: "",
        customerContactEmail: ""
    });
    const [newCustomerContact, setNewCustomerContact] = React.useState(false);
    const [newContact, setNewContact] = React.useState<AddContact>({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        mobile: ""
    });
    const [chargeAdminFee, setChargeAdminFee] = React.useState(true);
    const [hireStartDate, setHireStartDate] = React.useState("");
    const [restartHire, setRestartHire] = React.useState(-1);
    const [items, setItems] = React.useState<ItemListItem[]>([]);
    const [poNumber, setPoNumber] = React.useState("");
    const [custAcctType, setCustAcctType] = React.useState({
        accountTypeId: -1,
        accountType: "",
        accountLimitFormatted: ""
    });

    const hideModal = () => {
        resetModal();
        props.hideModal();
    }

    const clearErrors = (errorName: string) => {
        let errorList = errors;
        if (errorName) {
            delete errorList[errorName];
            setErrors(errorList);
        }
    }

    const handlecustomerChange = (e: any) => {
        clearErrors(e.target.name);
        //clear add new customer contact when changing customers
        setNewCustomerContact(false);
        setNewContact({
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            mobile: ""
        });
        //set customer contact details back to blank
        setCashSaleContact(prevState => ({ ...prevState, customerContactName: "", customerContactPhone: "", customerContactEmail: "" }));

        var custId = e.target.value * 1;
        if (custId > 0) {
            var customer = props.customers.find(c => c.id == custId);
            if (customer) {
                var typeId = customer.accountTypeId;
                var type = customer.accountType;
                var limitFormatted = customer.accountLimitFormatted;
                setCustAcctType(prevState => ({ ...prevState, accountTypeId: typeId, accountType: type, accountLimitFormatted: limitFormatted }));    
                setCustomerIsCashSale(customer.cashSaleAccount);

                getCustomerContacts(e.target.value, customer.cashSaleAccount);
            }
        } else {
            setCustomerId(custId);
            setCustomerContactId(-1);
            setCustomerContacts([]);
            setSaveDisabled(false);
        }
    }

    const getCustomerContacts = async (customerId: number, isCashSale: boolean) => {
        const token = await authService.getAccessToken();

        //get list of customer contacts from the database
        axios.get('api/Job/GetCustomerContact?customerId=' + customerId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        }).then(res => {
            setCustomerId(customerId);
            if (isCashSale) {
                //get first contact
                var contacts = res.data;
                if (contacts.length > 0) {
                    var firstContact = contacts[0];
                    setCustomerContactId(firstContact.id);
                }
            } else {
                setCustomerContactId(-1);
            }
            setCustomerContacts(res.data);
            setSaveDisabled(false);
        });
    }

    const handleCustomerContactChange = (e: any) => {
        clearErrors(e.target.name);
        var custContactId = e.target.value * 1;
        setCustomerContactId(custContactId);

        if (custContactId > 0) {
            setNewCustomerContact(false);
            setNewContact({
                firstName: "",
                lastName: "",
                email: "",
                phone: "",
                mobile: ""
            });
            //check to see if there is an email address, and if not then show edit contact box so user can add email
            var contact = customerContacts.find(cc => cc.id == custContactId);
            if (contact) {
                var email = contact.email;
                setNewContact(prevState => ({ ...prevState, "email": email }));
            }
        } else {
            clearContactEmail();
        }

        setSaveDisabled(false);
    }

    const clearContactEmail = () => {
        setNewContact(prevState => ({ ...prevState, "email": "" }));
        setCustomerContactId(-1);
    }

    const handleCashSaleContactChange = (e: any) => {
        clearErrors(e.target.name);
        setCashSaleContact(prevState => ({ ...prevState, [e.target.name]: e.target.value }));
        setSaveDisabled(false);
    }

    const handleAddContactChange = (e: any) => {
        clearErrors(e.target.name);
        setNewContact(prevState => ({ ...prevState, [e.target.name]: e.target.value }));
        setSaveDisabled(false);
    }

    const handleHireStartDateChange = (e: any) => {
        clearErrors(e.target.name);
        setHireStartDate(e.target.value);
        setSaveDisabled(false);
    }

    const handleRestartHireChange = (e: any) => {
        clearErrors(e.target.name);
        let value = e.target.value * 1;
        if (value === 1) {
            //need to get the items to restart the hire
            setRestartHire(value);           
            getJobItems();
        } else {
            setRestartHire(value);
            setSaveDisabled(false);
        }
    }

    const getJobItems = async () => {
        const token = await authService.getAccessToken();

        //get list of customer contacts from the database
        axios.get('api/Job/GetTransferJobItems?jobId=' + props.jobId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            setItems(res.data);
            setSaveDisabled(false);
        });
    }

    const handlePoNumberChange = (e: any, itemIndex: number) => {
        var itemList = [...items];
        var item = itemList[itemIndex];

        item.purchaseOrderNumber = e.target.value;
        setItems(itemList);
        setSaveDisabled(false);
    }

    const handleCellHirePeriodChange = (e: any, itemIndex: number) => {
        if (customerId < 1) {
            toast.error("Please select a new customer before selecting hire period");
        } else {
            let value = e.target.value * 1;
            calculateTransferJobPrice(value, itemIndex)
        }
    }

    const calculateTransferJobPrice = async (value: number, itemIndex: number) => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var itemList = [...items];
        var item = itemList[itemIndex];
        item.hirePeriod = value;

        axios.get('api/Job/CalculateTransferJobPrice?SubId=' + user.sub + '&CustomerId=' + customerId + '&JobQuoteItemId=' + item.jobQuoteItemId + '&Quantity=' + item.quantity + '&HirePeriod=' + value, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        }).then(res => {
            item.amount = res.data.initialHire; //need to use initial hire as item is already on site so no travel / cleaning etc
            item.overhireRate = res.data.overhireRate;
            setItems(itemList);
            setSaveDisabled(false);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const handleCellAmountChange = (e: any, itemIndex: number) => {
        var itemList = [...items];
        var editItem = itemList[itemIndex];
        editItem.amount = e.target.value * 1;
        setItems(itemList);
        setSaveDisabled(false);
    }

    const handleCellOverhireChange = (e: any, itemIndex: number) => {
        var itemList = [...items];
        var editItem = itemList[itemIndex];
        editItem.overhireRate = e.target.value * 1;
        setItems(itemList);
        setSaveDisabled(false);
    }

    const resetModal = () => {
        setErrors({});
        setSaveDisabled(false);
        setCustomerContacts([]);
        setCustomerId(-1);
        setCustomerContactId(-1);
        setNewCustomerContact(false);
        setNewContact({
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            mobile: ""
        });
        setRestartHire(-1);
        setItems([]);
    }

    const validate = () => {
        let errorList: any = {};
        let formIsValid = true;

        if (customerId < 1) {
            formIsValid = false;
            errorList["customerId"] = "Customer is required";
        }

        if (customerIsCashSale) {
            if (!cashSaleContact.customerContactName) {
                formIsValid = false;
                errorList["customerContactName"] = "Contact Name is required";
            }

            if (!cashSaleContact.customerContactPhone) {
                formIsValid = false;
                errorList["customerContactPhone"] = "Contact Phone is required";
            }

            if (!cashSaleContact.customerContactEmail) {
                formIsValid = false;
                errorList["customerContactEmail"] = "Contact Email is required";
            } else if (cashSaleContact.customerContactEmail.indexOf("@") < 0) {
                formIsValid = false;
                errorList["customerContactEmail"] = "Valid contact email is required";
            }
        } else {

            if (newCustomerContact) {
                if (!newContact.firstName) {
                    formIsValid = false;
                    errorList["firstName"] = "First Name is required";
                }

                if (!newContact.phone && !newContact.mobile) {
                    formIsValid = false;
                    errorList["phone"] = "Phone or Mobile is required";
                }
            } else if (customerContactId < 1) {
                formIsValid = false;
                errorList["customerContactId"] = "Contact or new contact is required";
            }

            if (!newContact.email) {
                formIsValid = false;
                errorList["email"] = "Email is required";
            } else if (newContact.email.indexOf("@") < 0) {
                formIsValid = false;
                errorList["email"] = "Valid email is required";
            }
        }

        if (!hireStartDate || hireStartDate === "") {
            formIsValid = false;
            errorList["hireStartDate"] = "Hire Start Date is required";
        }

        if (props.systemCompanyId === 3) {
            if (restartHire < 0) {
                formIsValid = false;
                errorList["restartHire"] = "Restart Hire required";
            } else if (restartHire === 1) {
                //validate items
                let errorItem = false;
                let rowItemNumber = 0;
                let errorString = "Please enter the following values: ";

                for (var i = 0; i < items.length; i++) {
                    rowItemNumber += 1;
                    var newErrorString = "";

                    if (items[i].hirePeriod <= 0) {
                        newErrorString += " Hire Period, ";
                        errorItem = true;
                    }

                    var itemAmount = items[i].amount * 1;
                    if (itemAmount <= 0) {
                        newErrorString += " Amount, ";
                        errorItem = true;
                    }

                    var overhireAmount = items[i].overhireRate * 1;
                    if (overhireAmount <= 0 && !items[i].installOnly) {
                        newErrorString += " Overhire Rate, ";
                        errorItem = true;
                    }

                    if (newErrorString != "") {
                        errorString += "\n";
                        errorString += "Row " + rowItemNumber + ": " + newErrorString;
                        errorString = errorString.substring(0, errorString.length - 2);
                    }
                }

                if (errorItem) {
                    formIsValid = false;
                    errorList["items"] = errorString;
                }
            }
        }

        setErrors(errorList);
        return formIsValid;
    }

    const saveTransferJob = () => {
        if (validate()) {
            saveTransfer();
        } else {
            toast.error("Fix errors on screen before transferring");
        }
    }

    const saveTransfer = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var transfer = {
            jobId: props.jobId,
            customerId: customerId,
            customerContactId: customerContactId,
            customerIsCashSale: customerIsCashSale,
            customerContactName: cashSaleContact.customerContactName,
            customerContactPhone: cashSaleContact.customerContactPhone,
            customerContactEmail: cashSaleContact.customerContactEmail,
            contactFirstName: newContact.firstName,
            contactLastName: newContact.lastName,
            contactPhone: newContact.phone,
            contactMobile: newContact.mobile,
            contactEmail: newContact.email,
            chargeAdminFee: chargeAdminFee,
            hireStartDate: hireStartDate,
            purchaseOrderNumber: poNumber,
            items: items,
            subId: user.sub
        };

        let hasItems = items.length > 0;

        //add new contact
        axios.post('api/Job/TransferJob', transfer, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                for (var i = 0; i < res.data.messages.length; i++) {
                    toast.error(res.data.messages[i].content);
                }
            } else {
                resetModal();
                var custContacts:CustomerContactList[] = [];
                if (!hasItems) {
                    custContacts = res.data.customerContacts;
                }
                props.saveModal(custContacts, res.data.jobId);
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    return (
        <Modal static size={'lg'} aria-labelledby="contained-modal-title-sm" show={props.showModal} onHide={hideModal}>
            <Modal.Header className="modal__header--colour">
                <Modal.Title>Transfer Job to New Customer</Modal.Title>
                <button type="button" className="close" onClick={hideModal}>&times;</button>
            </Modal.Header>
            <Modal.Body>
                <label className="input-group" htmlFor="customerId">
                    <span className="label">Customer </span>
                    <select className="select" id="customerId" name="customerId" value={customerId} onChange={(e) => handlecustomerChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.customers.map(cust =>
                            <option key={cust.id} value={cust.id}>{cust.name}</option>
                        )};
                    </select>
                </label>
                <span className={custAcctType.accountLimitFormatted === "" ? "hidden" : "marginLeftLabel"}>{custAcctType.accountTypeId === 1 ? custAcctType.accountType : custAcctType.accountType + ' - ' + custAcctType.accountLimitFormatted}</span>
                <span className={errors["customerId"] ? "label errors marginLeftLabel" : "hidden"}>{errors["customerId"]}</span>

                <label className={customerIsCashSale ? "hidden" : "input-group"} htmlFor="contact">
                    <span className="label">Contact</span>
                    <select className="select" id="contact" name="customerContactId" value={customerContactId} onChange={(e) => handleCustomerContactChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {customerContacts.map(custcon =>
                            <option key={custcon.id} value={custcon.id}>{custcon.name}</option>
                        )};
                    </select>
                </label>
                <span className={errors["customerContactId"] ? "label errors marginLeftLabel" : "hidden"}>{errors["customerContactId"]}</span>

                <div className={customerIsCashSale ? "" : "hidden"}>
                    <label className="input-group" htmlFor="customerContactName">
                        <span className="label">Contact Name</span>
                        <input className="input" type="text" id="customerContactName" name="customerContactName" value={cashSaleContact.customerContactName} onChange={(e) => handleCashSaleContactChange(e)}></input>
                    </label>
                    <span className={errors["customerContactName"] ? "label errors marginLeftLabel" : "hidden"}>{errors["customerContactName"]}</span>

                    <label className="input-group" htmlFor="customerContactPhone">
                        <span className="label">Contact Phone</span>
                        <input className="input" type="text" id="customerContactPhone" name="customerContactPhone" value={cashSaleContact.customerContactPhone} onChange={(e) => handleCashSaleContactChange(e)}></input>
                    </label>
                    <span className={errors["customerContactPhone"] ? "label errors marginLeftLabel" : "hidden"}>{errors["customerContactPhone"]}</span>

                    <label className="input-group" htmlFor="customerContactEmail">
                        <span className="label">Contact Email</span>
                        <input className="input" type="text" id="customerContactEmail" name="customerContactEmail" value={cashSaleContact.customerContactEmail} onChange={(e) => handleCashSaleContactChange(e)}></input>
                    </label>
                    <span className={errors["customerContactEmail"] ? "label errors marginLeftLabel" : "hidden"}>{errors["customerContactEmail"]}</span>
                </div>

                <label className={customerIsCashSale ? "hidden" : "input-group"} htmlFor="newCustomerContact">
                    <span className="label label--setsizesm">New Contact?</span>
                    <input className="checkbox" type="checkbox" id="newCustomerContact" name="newCustomerContact" checked={newCustomerContact} onChange={(e) => { clearContactEmail(); setNewCustomerContact(e.target.checked) }}></input>
                </label>

                <div className={newCustomerContact ? "" : "hidden"}>
                    <label className="input-group" htmlFor="firstName">
                        <span className="label">First Name</span>
                        <input className="input" type="text" id="firstName" name="firstName" value={newContact.firstName} onChange={(e) => handleAddContactChange(e)}></input>
                    </label>
                    <span className={errors["firstName"] ? "label errors marginLeftLabel" : "hidden"}>{errors["firstName"]}</span>

                    <label className="input-group" htmlFor="lastName">
                        <span className="label">Last Name</span>
                        <input className="input" type="text" id="lastName" name="lastName" value={newContact.lastName} onChange={(e) => handleAddContactChange(e)}></input>
                    </label>

                    <label className="input-group" htmlFor="phone">
                        <span className="label">Phone</span>
                        <input className="input" type="text" id="phone" name="phone" value={newContact.phone} onChange={(e) => handleAddContactChange(e)}></input>
                    </label>
                    <span className={errors["phone"] ? "label errors marginLeftLabel" : "hidden"}>{errors["phone"]}</span>

                    <label className="input-group" htmlFor="mobile">
                        <span className="label">Mobile</span>
                        <input className="input" type="text" id="mobile" name="mobile" value={newContact.mobile} onChange={(e) => handleAddContactChange(e)}></input>
                    </label>
                </div>

                <label className={customerIsCashSale ? "hidden" : "input-group"} htmlFor="email">
                    <span className="label">Email</span>
                    <input className="input" type="text" id="email" name="email" value={newContact.email} onChange={(e) => handleAddContactChange(e)}></input>
                </label>
                <span className={errors["email"] ? "label errors marginLeftLabel" : "hidden"}>{errors["email"]}</span>

                <label className="input-group" htmlFor="chargeAdminFee">
                    <span className="label label--setsizesm">Charge Transfer Fee?</span>
                    <input className="checkbox" type="checkbox" id="chargeAdminFee" name="chargeAdminFee" checked={chargeAdminFee} onChange={(e) => { setChargeAdminFee(e.target.checked) }}></input>
                </label>

                <div className="errors">Enter date that the new customer should be invoiced from. </div>
                <div className="errors">Note: any invoices after this date will be cancelled in DASH, accounts will need to CREDIT invoices in Reckon</div>
                <label className="input-group" htmlFor="scheduleDate">
                    <span className="label">Hire Start Date</span>
                    <input type='date' id="hireStartDate" name="hireStartDate" className='input' value={hireStartDate} onChange={(e) => handleHireStartDateChange(e)}></input>
                </label>
                <span className={errors["hireStartDate"] ? "label errors marginLeftLabel" : "hidden"}>{errors["hireStartDate"]}</span>

                <label className={props.systemCompanyId === 3 ? "input-group" : "hidden"} htmlFor="restartHire">
                    <span className="label">Restart Hire?</span>
                    <select className="select-small" id="restartHire" name="restartHire" value={restartHire} onChange={(e) => handleRestartHireChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        <option key={0} value={0}>No</option>
                        <option key={1} value={1}>Yes</option>
                    </select>
                </label>
                <span className={errors["restartHire"] ? "label errors marginLeftLabel" : "hidden"}>{errors["restartHire"]}</span>

                <div className={restartHire === 0 && custAcctType.accountTypeId === 3 ? "" : "hidden"}>
                    <label className="input-group" htmlFor="poNumber">
                        <span className="label">Purchase Order Number</span>
                        <input className="input" type="text" id="poNumber" name="poNumber" value={poNumber} onChange={(e) => setPoNumber(e.target.value)}></input>
                    </label>
                </div>

                <div className={restartHire === 1 ? "overflowAuto" : "hidden" }>
                    <table className="table tableColours">
                        <thead>
                            <tr>
                                <th></th>
                                <th>Item</th>
                                <th>Qty</th>
                                <th>PO</th>
                                <th>Hire Period</th>
                                <th>Amount</th>
                                <th>O/H Rate</th>
                            </tr>
                        </thead>
                        {items.map((item, index) =>
                            <tbody key={index}>
                                <tr key={index}>
                                    <td className="table__text--align">{index + 1}</td>
                                    <td className="table__text--align">{item.item}</td>
                                    <td className="table__text--align">{item.quantity}</td>
                                    <td className="table__text--align">
                                        <input type="text" maxLength={25} className="form-control allocate__dateSize" value={item.purchaseOrderNumber} onChange={(e) => handlePoNumberChange(e, index)} />
                                    </td>
                                    <td className="table__text--align">
                                        <select className="select table__select--size" id="hirePeriod" name="hirePeriod" value={item.hirePeriod} onChange={(e) => handleCellHirePeriodChange(e, index)}>
                                            <option hidden defaultValue="-1"></option>
                                            <option hidden={item.itemTypeId != 26} value={1}>1 Day</option>
                                            <option hidden={item.defaultHirePeriod > 7 || item.invoicePeriodTypeId === 2} value={7}>1 Week</option>
                                            <option hidden={item.defaultHirePeriod > 14 || item.invoicePeriodTypeId === 2} value={14}>2 Weeks</option>
                                            <option hidden={item.defaultHirePeriod > 21 || item.invoicePeriodTypeId === 2} value={21}>3 Weeks</option>
                                            <option hidden={item.defaultHirePeriod > 28 || item.invoicePeriodTypeId === 2} value={28}>4 Weeks</option>
                                            <option hidden={item.defaultHirePeriod > 35 || item.invoicePeriodTypeId === 2} value={35}>5 Weeks</option>
                                            <option hidden={item.itemTypeId === 19 || item.itemTypeId === 23 || item.defaultHirePeriod > 42 || item.invoicePeriodTypeId === 2} value={42}>6 Weeks</option>
                                            <option hidden={item.itemTypeId === 23 || item.itemTypeId === 25 || item.itemTypeId == 26 || item.defaultHirePeriod > 56 || item.invoicePeriodTypeId === 2} value={56}>8 Weeks</option>
                                            <option hidden={item.itemTypeId === 19 || item.itemTypeId === 23 || item.defaultHirePeriod > 70 || item.invoicePeriodTypeId === 2} value={70}>10 Weeks</option>
                                            <option hidden={item.invoicePeriodTypeId === 2} value={91}>13 Weeks</option>
                                            <option hidden={item.itemTypeId === 23 || item.itemTypeId === 50 || item.itemTypeId === 51} value={98}>14 Weeks</option>
                                            <option hidden={item.itemTypeId === 23 || item.itemTypeId === 50 || item.itemTypeId === 51} value={112}>16 Weeks</option>
                                            <option hidden={item.itemTypeId != 25 || item.invoicePeriodTypeId === 2} value={140}>20 Weeks</option>
                                            <option value={item.invoicePeriodTypeId === 2 ? 6 : 182}>{item.invoicePeriodTypeId === 2 ? "6 Months" : "26 Weeks"}</option>
                                            <option hidden={item.itemTypeId != 23} value={252}>36 Weeks</option>
                                            <option hidden={item.itemTypeId != 25} value={280}>40 Weeks</option>
                                            <option value={item.invoicePeriodTypeId === 2 ? 12 : 364}>{item.invoicePeriodTypeId === 2 ? "12 Months" : "52 Weeks"}</option>
                                        </select>
                                    </td>
                                    <td className="table__text--align">
                                        <input type='number' min="0" step="0.01" className='form-control textalignright transferjob-amount--size' id="amount" name="amount" value={item.amount} onChange={(e) => handleCellAmountChange(e, index)} />
                                    </td>
                                    <td className="table__text--align">
                                        <input type='number' min="0" step="0.01" className='form-control textalignright transferjob-amount--size' id="overhireRate" name="overhireRate" value={item.overhireRate} onChange={(e) => handleCellOverhireChange(e, index)} disabled />
                                    </td>
                                </tr>
                            </tbody>
                        )}
                    </table>
                    {errors["items"] ?
                        (errors["items"]).split("\n").map((item: any, key: any) => {
                            return <span className="label errors" key={key}>{item}<br /></span>
                        })
                        : ""}
                </div>
            </Modal.Body>

            <Modal.Footer>
                <button className="popup-button--yes" onClick={saveTransferJob} disabled={saveDisabled}>Transfer</button>
            </Modal.Footer>
        </Modal>
    );

}

export default JobDetailsTransferJobModalData;